
import React from 'react';
import { BlogPost } from '@/types/blog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Eye, Plus } from 'lucide-react';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

interface BlogPostsTableProps {
  posts: BlogPost[];
  onDelete: (id: string) => void;
  onToggleAutoSchedule: (id: string) => void;
  onEdit: (post: BlogPost) => void;
  onView: (post: BlogPost) => void;
  onCreate: () => void;
}

const BlogPostsTable: React.FC<BlogPostsTableProps> = ({
  posts,
  onDelete,
  onToggleAutoSchedule,
  onEdit,
  onView,
  onCreate
}) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-500">Published</Badge>;
      case 'draft':
        return <Badge variant="outline" className="border-amber-500 text-amber-500">Draft</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-500">Scheduled</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="rounded-md border">
      <div className="p-4 border-b">
        <Button className="flex items-center gap-1" onClick={onCreate}>
          <Plus className="h-4 w-4" /> Create New Post
        </Button>
      </div>
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI Schedule</TableHead>
            <TableHead className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="bg-white divide-y divide-gray-200">
          {posts.map((post) => (
            <TableRow key={post.id}>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="h-10 w-10 flex-shrink-0">
                    <img className="h-10 w-10 rounded object-cover" src={post.image} alt="" />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{post.title}</div>
                    <div className="text-sm text-gray-500">{post.readTime} read</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                {getStatusBadge(post.status)}
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{post.category}</div>
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{post.author}</div>
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{post.date}</div>
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap">
                <Button 
                  variant={post.autoScheduled ? "default" : "outline"}
                  size="sm"
                  onClick={() => onToggleAutoSchedule(post.id)}
                >
                  {post.autoScheduled ? "Enabled" : "Disabled"}
                </Button>
              </TableCell>
              <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onEdit(post)}
                    title="Edit post"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onDelete(post.id)}
                    title="Delete post"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onView(post)}
                    title="Preview post"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {posts.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="px-4 py-8 text-center">
                No blog posts found matching your search.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default BlogPostsTable;
